import React, { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReplayIcon from "@mui/icons-material/Replay";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "react-query";
import { ConfirmDialog } from "../common";
import {
  UploadItem,
  WaterBillFile,
  WaterBillSession,
  WaterBillSessionDetail,
  apiErrorMessage,
  createWaterBillSession,
  deleteWaterBillSession,
  downloadWaterBillExcel,
  getCurrentWaterBillSession,
  listWaterBillSessions,
  retryWaterBillSession,
  startWaterBillSession,
  stopWaterBillSession,
  uploadWaterBillFiles,
} from "../../services/waterBillsApi";

export const WATER_BILLS_COLOR = "#1E88E5";

const CURRENT_KEY = ["water-bills", "current"];
const HISTORY_KEY = ["water-bills", "history"];

// nginx accetta richieste fino a 100 MB: i caricamenti viaggiano a lotti ben sotto quel limite
const BATCH_MAX_BYTES = 40 * 1024 * 1024;
const BATCH_MAX_FILES = 30;
const SINGLE_FILE_MAX_BYTES = 95 * 1024 * 1024;

const isAccepted = (name: string) => /\.(pdf|zip)$/i.test(name);
const isHidden = (path: string) => path.split("/").some((p) => p.startsWith("."));

// ── raccolta dei file (selezione, trascinamento di file e cartelle) ─────────────

interface Collected {
  items: UploadItem[];
  ignored: number;
}

async function readAllEntries(reader: any): Promise<any[]> {
  const all: any[] = [];
  // readEntries restituisce al massimo ~100 voci per chiamata: va richiamata fino a esaurimento
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const batch: any[] = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) return all;
    all.push(...batch);
  }
}

async function walkEntry(entry: any, prefix: string, out: Collected): Promise<void> {
  if (entry.isFile) {
    const file: File = await new Promise((resolve, reject) => entry.file(resolve, reject));
    const path = prefix + entry.name;
    if (isAccepted(file.name) && !isHidden(path)) out.items.push({ file, path });
    else out.ignored += 1;
  } else if (entry.isDirectory) {
    const children = await readAllEntries(entry.createReader());
    for (const child of children) await walkEntry(child, prefix + entry.name + "/", out);
  }
}

async function collectFromDrop(dt: DataTransfer): Promise<Collected> {
  const out: Collected = { items: [], ignored: 0 };
  // webkitGetAsEntry va chiamato subito, durante l'evento: dopo il primo await gli item non sono più leggibili
  const entries: any[] = [];
  const plainFiles: File[] = [];
  if (dt.items && dt.items.length) {
    for (let i = 0; i < dt.items.length; i++) {
      const item: any = dt.items[i];
      if (item.kind !== "file") continue;
      const entry = item.webkitGetAsEntry?.();
      if (entry) entries.push(entry);
      else if (item.getAsFile) plainFiles.push(item.getAsFile());
    }
  } else {
    plainFiles.push(...Array.from(dt.files));
  }
  for (const entry of entries) await walkEntry(entry, "", out);
  for (const file of plainFiles) {
    if (file && isAccepted(file.name)) out.items.push({ file, path: file.name });
    else out.ignored += 1;
  }
  return out;
}

function collectFromInput(files: FileList | null): Collected {
  const out: Collected = { items: [], ignored: 0 };
  Array.from(files || []).forEach((file) => {
    const path = (file as any).webkitRelativePath || file.name;
    if (isAccepted(file.name) && !isHidden(path)) out.items.push({ file, path });
    else out.ignored += 1;
  });
  return out;
}

function makeBatches(items: UploadItem[]): UploadItem[][] {
  const batches: UploadItem[][] = [];
  let current: UploadItem[] = [];
  let size = 0;
  for (const item of items) {
    if (current.length && (size + item.file.size > BATCH_MAX_BYTES || current.length >= BATCH_MAX_FILES)) {
      batches.push(current);
      current = [];
      size = 0;
    }
    current.push(item);
    size += item.file.size;
  }
  if (current.length) batches.push(current);
  return batches;
}

// ── formattazione ────────────────────────────────────────────────────────────

const formatDuration = (seconds?: number | null) => {
  if (seconds == null) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${s % 60} s`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
};

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const STATUS_LABEL: Record<string, string> = {
  uploading: "In caricamento",
  running: "In elaborazione",
  completed: "Completata",
  error: "Errore",
};

const STATUS_COLOR: Record<string, "default" | "info" | "success" | "error"> = {
  uploading: "default",
  running: "info",
  completed: "success",
  error: "error",
};

// ── componenti ───────────────────────────────────────────────────────────────

interface DropZoneProps {
  compact?: boolean;
  disabled?: boolean;
  onCollected: (c: Collected) => void;
}

const DropZone: React.FC<DropZoneProps> = ({ compact, disabled, onCollected }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const stop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    stop(e);
    setDragging(false);
    if (disabled) return;
    onCollected(await collectFromDrop(e.dataTransfer));
  };

  return (
    <Box
      onClick={() => !disabled && fileRef.current?.click()}
      onDragOver={stop}
      onDragEnter={(e) => {
        stop(e);
        if (!disabled) setDragging(true);
      }}
      onDragLeave={(e) => {
        stop(e);
        setDragging(false);
      }}
      onDrop={handleDrop}
      sx={{
        border: "2px dashed",
        borderColor: dragging ? WATER_BILLS_COLOR : "rgba(0,0,0,0.18)",
        borderRadius: "14px",
        p: compact ? 2 : 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        bgcolor: dragging ? WATER_BILLS_COLOR + "14" : "background.paper",
        transition: "all 0.2s",
        "&:hover": disabled ? {} : { borderColor: WATER_BILLS_COLOR, bgcolor: WATER_BILLS_COLOR + "08" },
      }}
    >
      <CloudUploadOutlinedIcon sx={{ fontSize: compact ? "1.8rem" : "2.6rem", color: dragging ? WATER_BILLS_COLOR : "text.disabled" }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {dragging ? "Rilascia qui i file" : compact ? "Aggiungi altri file" : "Trascina qui le bollette"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        PDF, cartelle (anche con sottocartelle) o archivi ZIP
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          startIcon={<InsertDriveFileOutlinedIcon />}
          onClick={() => fileRef.current?.click()}
          sx={{ borderRadius: "8px", textTransform: "none", borderColor: WATER_BILLS_COLOR, color: WATER_BILLS_COLOR }}
        >
          Seleziona file
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          startIcon={<FolderOpenOutlinedIcon />}
          onClick={() => folderRef.current?.click()}
          sx={{ borderRadius: "8px", textTransform: "none", borderColor: WATER_BILLS_COLOR, color: WATER_BILLS_COLOR }}
        >
          Seleziona cartella
        </Button>
      </Box>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed"
        style={{ display: "none" }}
        onChange={(e) => {
          onCollected(collectFromInput(e.target.files));
          e.target.value = "";
        }}
      />
      <input
        ref={folderRef}
        type="file"
        style={{ display: "none" }}
        {...({ webkitdirectory: "", directory: "" } as any)}
        onChange={(e) => {
          onCollected(collectFromInput(e.target.files));
          e.target.value = "";
        }}
      />
    </Box>
  );
};

const KpiTile: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      minWidth: 120,
      p: 1.5,
      borderRadius: "12px",
      bgcolor: color ? color + "12" : "rgba(0,0,0,0.03)",
      border: "1px solid",
      borderColor: color ? color + "30" : "rgba(0,0,0,0.06)",
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: color || "text.primary" }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const FileStatusIcon: React.FC<{ file: WaterBillFile }> = ({ file }) => {
  if (file.status === "done") {
    return file.alerts_count > 0 ? (
      <WarningAmberIcon sx={{ fontSize: "1.1rem", color: "#F9A825" }} />
    ) : (
      <CheckCircleOutlineIcon sx={{ fontSize: "1.1rem", color: "#43A047" }} />
    );
  }
  if (file.status === "error") return <ErrorOutlineIcon sx={{ fontSize: "1.1rem", color: "#E53935" }} />;
  if (file.status === "processing") return <CircularProgress size={14} sx={{ color: WATER_BILLS_COLOR }} />;
  return <HourglassEmptyIcon sx={{ fontSize: "1.1rem", color: "text.disabled" }} />;
};

const FileList: React.FC<{ files: WaterBillFile[]; showResults: boolean }> = ({ files, showResults }) => (
  <Box sx={{ maxHeight: 240, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: "10px" }}>
    {files.map((f) => (
      <Box
        key={f.id}
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.6, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: "none" } }}
      >
        {showResults ? <FileStatusIcon file={f} /> : <InsertDriveFileOutlinedIcon sx={{ fontSize: "1.1rem", color: "text.disabled" }} />}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap title={f.filename}>
            {f.filename}
          </Typography>
          {f.status === "error" && f.error_message && (
            <Typography variant="caption" color="error">
              {f.error_message}
              {showResults && !f.retryable ? " Per includerlo, caricalo di nuovo in una nuova sessione." : ""}
            </Typography>
          )}
        </Box>
        {showResults && f.status === "done" && f.alerts_count > 0 && (
          <Chip size="small" label={`${f.alerts_count} alert`} sx={{ bgcolor: "#FFD96660", fontWeight: 600 }} />
        )}
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
          {formatSize(f.size_bytes)}
        </Typography>
      </Box>
    ))}
  </Box>
);

// ── strumento ────────────────────────────────────────────────────────────────

export const WaterBillsTool: React.FC = () => {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState<{ pct: number; done: number; total: number } | null>(null);
  const [notice, setNotice] = useState<{ severity: "info" | "warning" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WaterBillSession | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);

  const { data: session, isLoading } = useQuery<WaterBillSessionDetail | null>(CURRENT_KEY, getCurrentWaterBillSession, {
    // finché qualcuno carica o l'elaborazione è in corso, lo stato viene riletto dal server
    refetchInterval: (data) => (data && (data.status === "running" || data.status === "uploading") ? 3000 : false),
  });
  const { data: history = [] } = useQuery<WaterBillSession[]>(HISTORY_KEY, listWaterBillSessions, {
    refetchInterval: session?.status === "running" ? 5000 : false,
  });

  const refresh = useCallback(async () => {
    await Promise.all([queryClient.invalidateQueries(CURRENT_KEY), queryClient.invalidateQueries(HISTORY_KEY)]);
  }, [queryClient]);

  const ensureSession = async (): Promise<WaterBillSessionDetail> => {
    if (session && session.status === "uploading") return session;
    const created = await createWaterBillSession();
    queryClient.setQueryData(CURRENT_KEY, created);
    return created;
  };

  const handleCollected = async (collected: Collected) => {
    setNotice(null);
    const { items, ignored } = collected;
    if (items.length === 0) {
      setNotice({
        severity: "warning",
        text: ignored > 0 ? "Nessun PDF o ZIP trovato tra i file selezionati." : "Nessun file selezionato.",
      });
      return;
    }
    const tooBig = items.filter((i) => i.file.size > SINGLE_FILE_MAX_BYTES);
    const valid = items.filter((i) => i.file.size <= SINGLE_FILE_MAX_BYTES);
    const messages: string[] = [];
    if (tooBig.length) {
      messages.push(
        `${tooBig.length === 1 ? `"${tooBig[0].path}" supera` : `${tooBig.length} file superano`} i 95 MB e non ${
          tooBig.length === 1 ? "è stato caricato" : "sono stati caricati"
        }.`
      );
    }
    if (ignored > 0) messages.push(`${ignored} file non PDF/ZIP ignorati.`);
    if (valid.length === 0) {
      setNotice({ severity: "error", text: messages.join(" ") });
      return;
    }

    setBusy(true);
    try {
      const target = await ensureSession();
      const batches = makeBatches(valid);
      const totalBytes = valid.reduce((s, i) => s + i.file.size, 0) || 1;
      let sentBefore = 0;
      let doneFiles = 0;
      setUploading({ pct: 0, done: 0, total: valid.length });
      for (const batch of batches) {
        const batchBytes = batch.reduce((s, i) => s + i.file.size, 0);
        const baseBytes = sentBefore;
        const baseFiles = doneFiles;
        const result = await uploadWaterBillFiles(target.id, batch, (loaded) =>
          setUploading({ pct: Math.min(99, ((baseBytes + Math.min(loaded, batchBytes)) / totalBytes) * 100), done: baseFiles, total: valid.length })
        );
        sentBefore += batchBytes;
        doneFiles += batch.length;
        if (result.ignored > 0) messages.push(`${result.ignored} file non PDF ignorati negli archivi.`);
        result.unreadable_archives.forEach((a) => messages.push(`L'archivio "${a}" non è leggibile o è protetto da password.`));
        result.errors.forEach((m) => messages.push(m));
      }
      await refresh();
      if (messages.length) setNotice({ severity: "warning", text: messages.join(" ") });
    } catch (err: any) {
      setNotice({ severity: "error", text: apiErrorMessage(err, "Caricamento non riuscito. Riprova.") });
      await refresh();
    } finally {
      setUploading(null);
      setBusy(false);
    }
  };

  const handleStart = async () => {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      const started = await startWaterBillSession(session.id);
      queryClient.setQueryData(CURRENT_KEY, started);
      await queryClient.invalidateQueries(HISTORY_KEY);
    } catch (err: any) {
      setNotice({ severity: "error", text: apiErrorMessage(err, "Impossibile avviare l'elaborazione.") });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    setShowFiles(false);
    try {
      const started = await retryWaterBillSession(session.id);
      queryClient.setQueryData(CURRENT_KEY, started);
      await queryClient.invalidateQueries(HISTORY_KEY);
    } catch (err: any) {
      setNotice({ severity: "error", text: apiErrorMessage(err, "Impossibile riprovare i file falliti.") });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    if (!session) return;
    setBusy(true);
    setNotice(null);
    try {
      const stopping = await stopWaterBillSession(session.id);
      queryClient.setQueryData(CURRENT_KEY, stopping);
      toast.success("Interruzione richiesta: l'elaborazione si sta fermando.");
    } catch (err: any) {
      setNotice({ severity: "error", text: apiErrorMessage(err, "Impossibile interrompere l'elaborazione.") });
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  // se ci sono file ancora rielaborabili, una nuova sessione li rende non più recuperabili: chiede conferma
  const handleNewClick = () => {
    if (session?.retry_available) setConfirmNew(true);
    else void handleNew();
  };

  const handleNew = async () => {
    setBusy(true);
    setNotice(null);
    setShowFiles(false);
    try {
      const created = await createWaterBillSession();
      queryClient.setQueryData(CURRENT_KEY, created);
      await queryClient.invalidateQueries(HISTORY_KEY);
    } catch (err: any) {
      setNotice({ severity: "error", text: apiErrorMessage(err, "Impossibile avviare un nuovo caricamento.") });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!session) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadWaterBillExcel(session.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // con responseType blob il messaggio di errore arriva come Blob
      let text = "Download non riuscito.";
      try {
        const body = JSON.parse(await err?.response?.data?.text?.());
        if (typeof body?.detail === "string") text = body.detail;
      } catch {
        /* resta il messaggio generico */
      }
      toast.error(text);
      await refresh();
    } finally {
      setDownloading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteWaterBillSession(deleteTarget.id);
      toast.success("Sessione eliminata");
      await refresh();
    } catch (err: any) {
      toast.error(apiErrorMessage(err, "Eliminazione non riuscita"));
    } finally {
      setDeleteTarget(null);
      setBusy(false);
    }
  };

  // ── rendering dello stato corrente ───────────────────────────────────────

  const files = session?.files ?? [];
  const pct = session && session.total_files > 0 ? (session.processed_files / session.total_files) * 100 : 0;

  const renderCurrent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: WATER_BILLS_COLOR }} />
        </Box>
      );
    }

    // nessuna sessione, oppure caricamento appena aperto: area di caricamento
    if (!session) {
      return <DropZone disabled={busy} onCollected={handleCollected} />;
    }

    if (session.status === "uploading") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DropZone compact={files.length > 0} disabled={busy} onCollected={handleCollected} />
          {files.length > 0 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {files.length} {files.length === 1 ? "file pronto" : "file pronti"} per l'elaborazione
                </Typography>
                {session.created_by_name && (
                  <Typography variant="caption" color="text.secondary">
                    Caricamento di {session.created_by_name}
                  </Typography>
                )}
              </Box>
              <FileList files={files} showResults={false} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  "& .MuiButton-root": {
                    flex: { sm: 1 },
                    minWidth: 0,
                    px: 2.5,
                    py: 1.25,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                  },
                }}
              >
                <Button
                  variant="contained"
                  disabled={busy}
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStart}
                  sx={{ bgcolor: WATER_BILLS_COLOR, "&:hover": { bgcolor: "#1565C0" } }}
                >
                  Avvia elaborazione
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={busy}
                  onClick={() => setDeleteTarget(session)}
                >
                  Annulla caricamento
                </Button>
              </Box>
            </>
          )}
        </Box>
      );
    }

    if (session.status === "running") {
      return (
        <Box sx={{ p: 2.5, borderRadius: "14px", bgcolor: "background.paper", border: "1px solid", borderColor: "divider", display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={20} sx={{ color: WATER_BILLS_COLOR }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Elaborazione in corso
            </Typography>
            <Chip size="small" color="info" label={STATUS_LABEL.running} />
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 8, borderRadius: 4, "& .MuiLinearProgress-bar": { bgcolor: WATER_BILLS_COLOR } }}
          />
          <Typography variant="body2" color="text.secondary">
            {session.processed_files} di {session.total_files} file elaborati
            {session.failed_files > 0 ? ` (${session.failed_files} con errori)` : ""}
            {session.created_by_name ? ` — avviata da ${session.created_by_name}` : ""}.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Puoi chiudere questa finestra: l'elaborazione prosegue sul server e al ritorno ritroverai lo stato aggiornato.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<StopCircleOutlinedIcon />}
            disabled={busy}
            onClick={() => setConfirmStop(true)}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Interrompi elaborazione
          </Button>
          {files.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setShowFiles((v) => !v)}
                endIcon={<ExpandMoreIcon sx={{ transform: showFiles ? "rotate(180deg)" : "none", transition: "0.2s" }} />}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                Dettaglio file
              </Button>
              <Collapse in={showFiles}>
                <FileList files={files} showResults />
              </Collapse>
            </>
          )}
        </Box>
      );
    }

    // completed | error
    const isError = session.status === "error";
    const processedOk = Math.max(0, session.total_files - session.failed_files);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {isError ? (
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon />}
            sx={{ borderRadius: "10px" }}
            action={
              <Tooltip title={session.error_message || "Errore durante l'elaborazione"} arrow>
                <Chip size="small" color="error" variant="outlined" label="Dettaglio errore" sx={{ cursor: "help" }} />
              </Tooltip>
            }
          >
            L'elaborazione non è andata a buon fine. {session.error_message}
          </Alert>
        ) : (
          session.failed_files > 0 ? (
            <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ borderRadius: "10px" }}>
              <strong>
                {session.failed_files === 1 ? "1 file non è stato elaborato" : `${session.failed_files} file non sono stati elaborati`}
              </strong>
              {session.excel_available
                ? " e non è incluso nell'Excel: i risultati sono parziali. Controlla il dettaglio file."
                : ". Controlla il dettaglio file."}
              {session.retry_available ? " Puoi riprovare solo i file falliti: quelli già elaborati non vengono rifatti." : ""}
            </Alert>
          ) : (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ borderRadius: "10px" }}>
              Elaborazione completata.
            </Alert>
          )
        )}

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <KpiTile label="File processati" value={`${processedOk}/${session.total_files}`} color="#43A047" />
          <KpiTile label="Bollette estratte" value={session.bills_count} color={WATER_BILLS_COLOR} />
          <KpiTile label="File con alert" value={session.alert_files} color={session.alert_files > 0 ? "#F9A825" : undefined} />
          <KpiTile label="File non elaborati" value={session.failed_files} color={session.failed_files > 0 ? "#E53935" : undefined} />
          <KpiTile label="Durata" value={formatDuration(session.duration_seconds)} />
        </Box>

        {session.alert_files > 0 && !isError && (
          <Alert severity="warning" sx={{ borderRadius: "10px" }}>
            <strong>{session.alert_files}</strong> {session.alert_files === 1 ? "file contiene" : "file contengono"} righe con un
            avviso nella colonna <strong>CONTROLLI</strong> dell'Excel: richiedono una verifica manuale.
          </Alert>
        )}

        {files.length > 0 && (
          <>
            <Button
              size="small"
              onClick={() => setShowFiles((v) => !v)}
              endIcon={<ExpandMoreIcon sx={{ transform: showFiles ? "rotate(180deg)" : "none", transition: "0.2s" }} />}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Dettaglio file
            </Button>
            <Collapse in={showFiles}>
              <FileList files={files} showResults />
            </Collapse>
          </>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            "& .MuiButton-root": {
              flex: { sm: 1 },
              minWidth: 0,
              px: 2.5,
              py: 1.25,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            },
          }}
        >
          {session.excel_available && (
            <Button
              variant="contained"
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={18} sx={{ color: "white" }} /> : <DownloadIcon />}
              onClick={handleDownload}
              sx={{ bgcolor: WATER_BILLS_COLOR, "&:hover": { bgcolor: "#1565C0" } }}
            >
              {session.failed_files > 0 ? "Scarica Excel (parziale)" : "Scarica Excel"}
            </Button>
          )}
          {session.retry_available && (
            <Button
              variant="contained"
              color="warning"
              disabled={busy}
              startIcon={<ReplayIcon />}
              onClick={handleRetry}
            >
              {session.retryable_files === 1 ? "Riprova il file fallito" : `Riprova i ${session.retryable_files} file falliti`}
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={busy}
            startIcon={<AddIcon />}
            onClick={handleNewClick}
            sx={{
              borderColor: WATER_BILLS_COLOR,
              color: WATER_BILLS_COLOR,
              "&:hover": { borderColor: "#1565C0", bgcolor: "rgba(30, 136, 229, 0.04)" },
            }}
          >
            Nuovo caricamento
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 760, display: "flex", flexDirection: "column", gap: 3 }}>
      {uploading && (
        <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Caricamento in corso… {uploading.done}/{uploading.total} file
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploading.pct}
            sx={{ height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: WATER_BILLS_COLOR } }}
          />
        </Box>
      )}

      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ borderRadius: "10px" }}>
          {notice.text}
        </Alert>
      )}

      {renderCurrent()}

      {/* Storico */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Storico sessioni
        </Typography>
        {history.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Nessuna sessione precedente.
          </Typography>
        ) : (
          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: "12px", overflow: "hidden" }}>
            {history.map((h) => (
              <Box
                key={h.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" noWrap>
                    {formatDate(h.started_at || h.created_at)}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {" · "}
                      {h.created_by_name || "—"}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.status === "uploading"
                      ? "Caricamento non ancora avviato"
                      : `${h.total_files} file · ${h.bills_count} bollette · ${h.alert_files} con alert · ${h.failed_files} falliti · ${formatDuration(h.duration_seconds)}`}
                  </Typography>
                </Box>
                <Tooltip title={h.error_message || ""} arrow disableHoverListener={!h.error_message}>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[h.status] || "default"}
                    variant={h.status === "completed" ? "filled" : "outlined"}
                    label={STATUS_LABEL[h.status] || h.status}
                    sx={{ cursor: h.error_message ? "help" : "default" }}
                  />
                </Tooltip>
                <Tooltip title={h.status === "running" ? "Non eliminabile durante l'elaborazione" : "Elimina"}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={h.status === "running" || busy}
                      onClick={() => setDeleteTarget(h)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={confirmNew}
        loading={busy}
        onCancel={() => setConfirmNew(false)}
        onConfirm={async () => {
          setConfirmNew(false);
          await handleNew();
        }}
        title="Iniziare un nuovo caricamento?"
        content="Ci sono file non elaborati che puoi ancora riprovare. Iniziando un nuovo caricamento questa sessione non sarà più recuperabile e dovrai ricaricare quei file."
        confirmText="Nuovo caricamento"
        cancelText="Annulla"
      />

      <ConfirmDialog
        open={confirmStop}
        loading={busy}
        onCancel={() => setConfirmStop(false)}
        onConfirm={async () => {
          setConfirmStop(false);
          await handleStop();
        }}
        title="Interrompere l'elaborazione?"
        content="L'elaborazione si ferma subito. I file già elaborati vengono conservati; quelli non completati potranno essere riprovati."
        confirmText="Interrompi"
        cancelText="Continua"
      />

      <ConfirmDialog
        open={!!deleteTarget}
        loading={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Conferma eliminazione"
        content={
          deleteTarget?.status === "uploading"
            ? "Scartare i file caricati in questa sessione?"
            : "Eliminare questa sessione dallo storico? L'operazione non si può annullare."
        }
        confirmText="Elimina"
        cancelText="Annulla"
      />
    </Box>
  );
};
